import ModalHeader from '../../components/common/modals/ModalHeader';
import ModalBody from '../../components/common/modals/ModalBody';
import { Row, Col, Input, FormGroup, FormGroupLabel } from '@app/ui';
import ModalFooter from '../../components/common/modals/ModalFooter';
import Button from '@app/ui/sports-components/Button';
import Modal from '../../components/common/modals/Modal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchSelect from '@app/ui/sports-components/SearchSelect';
import PropTypes from 'prop-types';
import fetchSponsors from '../../actions/settings/fetchSponsors';
import connect from '../../utils/connect';
import combineActions from '../../utils/combineActions';
import sendNotification from '../../actions/notifications/send';
import sportsApi from '../../utils/sportsApi';
import AccountTag from './AccountTag';

const TextTrackingModal = ({
    client,
    rightsholders,
    onClose,
    sendNotification,
    updateTextTrackingFilters,
}) => {
    const { t } = useTranslation('settings');
    const [sponsorOptions, setSponsorOptions] = useState([]);
    const [sponsorsLoading, setSponsorsLoading] = useState(false);
    const [rightsholder, setRightsholder] = useState(null);
    const [sponsor, setSponsor] = useState(null);
    const [trackedText, setTrackedText] = useState(null);
    const [isLoading, setIsloading] = useState(false);

    const rightsholderOptions = useMemo(
        () =>
            rightsholders.map(r => ({
                value: r.id,
                name: r.name,
            })),
        [rightsholders],
    );

    const activeAccounts = useMemo(() => {
        if (!rightsholder) return null;

        return rightsholders
            .filter(rh => rh.id == rightsholder)
            .flatMap(rh => rh.accounts)
            .filter(a => a.activeSocialFilter);
    }, [rightsholder, rightsholders]);

    useEffect(() => {
        (async () => {
            if (!rightsholder) return;
            const sponsorOptions = await fetchSponsors({
                clientId: client.id,
                rightsholderId: rightsholder,
                setLoading: setSponsorsLoading,
            });
            setSponsorOptions(sponsorOptions);
        })();
    }, [rightsholder]);

    const addTrackedText = useCallback(() => {
        (async () => {
            if (!rightsholder || !sponsor || !trackedText) {
                sendNotification(
                    t(
                        'clientConfiguration.textTracking.modal.validation.required',
                    ),
                    { context: 'warning' },
                );
                return;
            }
            if (trackedText.length > 50) {
                sendNotification(
                    t(
                        'clientConfiguration.textTracking.modal.validation.maxLength',
                    ),
                    { context: 'warning' },
                );
                return;
            }
            setIsloading(true);
            await sportsApi
                .url(`/clients/${client.id}/text-tracking-filters `)
                .json({
                    action: 'create',
                    domainObject: {
                        text: trackedText,
                        rightsholderId: rightsholder,
                        sponsorId: sponsor,
                    },
                })
                .post()
                .res(() => {
                    updateTextTrackingFilters();
                    sendNotification(
                        t('clientConfiguration.textTracking.modal.success'),
                    );
                    onClose();
                })
                .catch(() => {
                    sendNotification(
                        t('clientConfiguration.textTracking.modal.error'),
                        { context: 'danger' },
                    );
                })
                .finally(() => {
                    setIsloading(false);
                });
        })();
    }, [
        rightsholder,
        sponsor,
        trackedText,
        sendNotification,
        client,
        onClose,
        t,
    ]);

    const onTextChanged = useCallback(
        e => {
            setTrackedText(e.target.value);
        },
        [trackedText],
    );

    return (
        <Modal data-test="create-modal">
            <ModalHeader
                title={t('clientConfiguration.textTracking.modal.title')}
                onClose={onClose}
            />
            <ModalBody
                className="-overflow-y-scroll"
                style={{
                    maxHeight: '500px',
                }}
            >
                <Row gutter={16}>
                    <FormGroup layout="vertical">
                        <FormGroupLabel>
                            <span className="-color-tx-lt-3">
                                {t(
                                    'clientConfiguration.textTracking.modal.textLabel',
                                )}
                            </span>
                        </FormGroupLabel>
                        <Input
                            name="text"
                            size="small"
                            onChange={onTextChanged}
                            disabled={isLoading}
                        />
                    </FormGroup>
                    <span className="gds-text--body-sm">
                        <i className="fas fa-info-circle -m-h-1 gds-text--info" />
                        <span className="gds-text--bold gds-text--info -m-r-1">
                            {t('clientConfiguration.textTracking.modal.note')}
                        </span>
                        {t('clientConfiguration.textTracking.modal.noteText')}
                    </span>
                </Row>
                <Row gutter={16}>
                    <FormGroup layout="vertical">
                        <FormGroupLabel>
                            <span className="-color-tx-lt-3">
                                {t(
                                    'clientConfiguration.textTracking.modal.rightsholderLabel',
                                )}
                            </span>
                        </FormGroupLabel>
                        <SearchSelect
                            name="rightsholder"
                            size="sm"
                            hideClearButton
                            options={rightsholderOptions}
                            onChange={setRightsholder}
                            value={rightsholder}
                            disabled={isLoading}
                        />
                    </FormGroup>
                </Row>
                <Row gutter={16}>
                    <FormGroup layout="vertical">
                        <FormGroupLabel>
                            <span className="-color-tx-lt-3">
                                {t(
                                    'clientConfiguration.textTracking.modal.sponsorLabel',
                                )}
                            </span>
                        </FormGroupLabel>
                        <SearchSelect
                            name="sponsor"
                            size="sm"
                            hideClearButton
                            disabled={
                                isLoading ||
                                sponsorsLoading ||
                                !Array.isArray(sponsorOptions) ||
                                !rightsholder
                            }
                            options={sponsorOptions}
                            onChange={setSponsor}
                            value={sponsor}
                        />
                    </FormGroup>
                </Row>
                <Row gutter={16}>
                    <Col className="-p-t-3">
                        {activeAccounts && activeAccounts.length == 0 && (
                            <span className="-color-tx-lt-4">
                                <span className="gds-text--bold gds-text--warning">
                                    {t(
                                        'clientConfiguration.textTracking.modal.warning',
                                    )}
                                </span>{' '}
                                {t(
                                    'clientConfiguration.textTracking.modal.missingAccountSetup',
                                )}
                            </span>
                        )}
                        {activeAccounts && activeAccounts.length > 0 && (
                            <div>
                                <span className="gds-text--bold -color-tx-lt-4 gds-text--body-sm">
                                    {t(
                                        'clientConfiguration.textTracking.modal.trackedOnAccounts',
                                    )}
                                </span>
                                <div className="-m-l-2">
                                    {activeAccounts.map(account => (
                                        <AccountTag
                                            key={
                                                account.username +
                                                account.platformId
                                            }
                                            account={account}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Row gutter={16}>
                    <Button
                        size="sm"
                        context="danger"
                        className="-m-b-0 -p-h-3"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t('clientConfiguration.onboarding.cancel')}
                    </Button>
                    <Button
                        size="sm"
                        context="tertiary"
                        className="-m-b-0 -m-l-3 -p-h-5"
                        onClick={addTrackedText}
                        disabled={isLoading}
                    >
                        {t('clientConfiguration.textTracking.modal.add')}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

TextTrackingModal.propTypes = {
    client: PropTypes.object.isRequired,
    rightsholders: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    sendNotification: PropTypes.func.isRequired,
    updateTextTrackingFilters: PropTypes.func.isRequired,
};

export default connect(
    ['client'],
    combineActions({
        sendNotification,
    }),
)(TextTrackingModal);
